/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable max-statements */
import { useCallback, useMemo } from 'react';
import type { useRelevantValueFromProps } from './useRelevantValueFromProps.js';
import type { useGetDestructuredValues } from './useGetDestructuredValues.js';
import type { useGetPropsBasedOnType } from './useGetPropsBasedOnType.js';
import type { useGetReferences } from './useGetReferences.js';
import type { useFocusTracker } from './useFocusTracker.js';
import { getValidationDateStringMetaInfo } from '../utils/dateHelpers.js';
import { getValidationTimeStringMetaInfo } from '../utils/timeHelpers.js';
import {
  isEmptyDateStringMask,
  isEmptyTimeStringMask,
  isEmptyDateTimeStringMask,
  getFormattedDateString,
  getFormattedTimeString,
} from '../utils/stringHelpers.js';
import type { DSControlledDateTimePickerT } from '../react-desc-prop-types.js';
import type { DSControlledDateTimePickerInternalsT } from '../sharedTypes.js';
import { defaultMetaInfo } from '../utils/constants.js';
interface Config {
  relevantValues: ReturnType<typeof useRelevantValueFromProps>;
  destructuredValues: ReturnType<typeof useGetDestructuredValues>;
  propsBasedOnType: ReturnType<typeof useGetPropsBasedOnType>;
  DOMRefs: ReturnType<typeof useGetReferences>;
  focusTrackers: ReturnType<typeof useFocusTracker>;
}

export const useChangeHandlers = ({ relevantValues, destructuredValues, propsBasedOnType, focusTrackers }: Config) => {
  const { tempDateStringRef, tempTimeStringRef } = relevantValues;
  const {
    getIsDisabledDay,
    getIsOutOfRangeDay,
    appOnDateChange,
    appOnMonthChange,
    appOnDayChange,
    appOnYearChange,
    getIsDisabledTime,
    appOnTimeChange,
    appOnHourChange,
    appOnMinuteChange,
    appOnMeridiemChange,
    appOnDateTimeChange,
    isDateTime,
    isDate,
    isTime,
  } = propsBasedOnType;
  const { month, day, year, hours, minutes, meridiem } = destructuredValues;
  const { trackFocusFirstSegment } = focusTrackers;

  const getDateMetaInfo = useCallback(
    (
      newDateString: string | null = null,
      metaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> = {},
    ): Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> => {
      const finalDateString = newDateString || tempDateStringRef.current;
      const finalMetaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> = {
        ...metaInfo,
        ...getValidationDateStringMetaInfo(finalDateString, getIsOutOfRangeDay, getIsDisabledDay),
      };
      return finalMetaInfo;
    },
    [tempDateStringRef, getIsDisabledDay, getIsOutOfRangeDay],
  );

  const getTimeMetaInfo = useCallback(
    (
      newTimeString: string | null = null,
      metaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> = {},
    ): Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> => {
      const finalTimeString = newTimeString || tempTimeStringRef.current;
      const finalMetaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> = {
        ...metaInfo,
        ...getValidationTimeStringMetaInfo(finalTimeString, getIsDisabledTime),
      };
      return finalMetaInfo;
    },
    [tempTimeStringRef, getIsDisabledTime],
  );

  const handleChangeComposedDateTimeString = useCallback(
    (
      newDateString: string,
      newTimeString: string,
      metaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo,
    ): void => {
      const appDateString = isEmptyDateStringMask(newDateString) ? '' : newDateString;
      const appTimeString = isEmptyTimeStringMask(newTimeString) ? '' : newTimeString;
      appOnDateChange(appDateString, metaInfo); // don't do this on useEffect to avoid complete chaos
      tempDateStringRef.current = newDateString; // use ref to avoid re-render

      appOnTimeChange(appTimeString, metaInfo); // don't do this on useEffect to avoid complete chaos
      tempTimeStringRef.current = newTimeString;

      const newDateTimeString = `${newDateString} ${newTimeString}`;
      const newMetaInfo = {
        ...metaInfo,
        ...getTimeMetaInfo(newTimeString, metaInfo),
        ...getDateMetaInfo(newDateString, metaInfo),
        newDateString,
        newTimeString,
        newDateTimeString,
      };

      const appDateTimeString = isEmptyDateTimeStringMask(newDateTimeString) ? '' : newDateTimeString;
      appOnDateTimeChange(appDateTimeString, newMetaInfo); // don't do this on useEffect to avoid complete chaos
    },
    [
      appOnDateChange,
      tempDateStringRef,
      appOnTimeChange,
      tempTimeStringRef,
      getTimeMetaInfo,
      getDateMetaInfo,
      appOnDateTimeChange,
    ],
  );
  const handleChangeComposedDateString = useCallback(
    (newDateString: string, metaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo>) => {
      // if we receive an "empty" mask, we comunicate empty string to the app
      const appDateString = isEmptyDateStringMask(newDateString) ? '' : newDateString;
      appOnDateChange(appDateString, metaInfo); // don't do this on useEffect to avoid complete chaos
      tempDateStringRef.current = newDateString;
      const newDateTimeString = `${newDateString} ${tempTimeStringRef.current}`;
      const newMetaInfo = {
        ...metaInfo,
        ...getTimeMetaInfo(tempTimeStringRef.current, metaInfo),
        ...getDateMetaInfo(newDateString, metaInfo),
        newDateString,
        newTimeString: tempTimeStringRef.current,
        newDateTimeString,
      };

      const appDateTimeString = isEmptyDateTimeStringMask(newDateTimeString) ? '' : newDateTimeString;
      appOnDateTimeChange(appDateTimeString, newMetaInfo); // don't do this on useEffect to avoid complete chaos
    },
    [appOnDateChange, tempDateStringRef, tempTimeStringRef, getTimeMetaInfo, getDateMetaInfo, appOnDateTimeChange],
  );
  const handleChangeComposedTimeString = useCallback(
    (newTimeString: string, metaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo>) => {
      const appTimeString = isEmptyTimeStringMask(newTimeString) ? '' : newTimeString;
      appOnTimeChange(appTimeString, metaInfo); // don't do this on useEffect to avoid complete chaos

      tempTimeStringRef.current = newTimeString;
      const newDateTimeString = `${tempDateStringRef.current} ${newTimeString}`;
      const newMetaInfo = {
        ...metaInfo,
        ...getTimeMetaInfo(newTimeString, metaInfo),
        ...getDateMetaInfo(tempDateStringRef.current, metaInfo),
        newDateTimeString,
        newDateString: tempDateStringRef.current,
        newTimeString,
      };

      const appDateTimeString = isEmptyDateTimeStringMask(newDateTimeString) ? '' : newDateTimeString;
      appOnDateTimeChange(appDateTimeString, newMetaInfo); // don't do this on useEffect to avoid complete chaos
    },
    [appOnTimeChange, tempTimeStringRef, tempDateStringRef, getTimeMetaInfo, getDateMetaInfo, appOnDateTimeChange],
  );

  const handleChangeMonth: DSControlledDateTimePickerT.OnInternalInputsChange = useCallback(
    (newMonth, e, metaInfo = defaultMetaInfo) => {
      const newDateString = getFormattedDateString({
        month: newMonth,
        day,
        year,
      });
      const finalMetaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo = {
        ...metaInfo,
        ...getValidationDateStringMetaInfo(newDateString, getIsOutOfRangeDay, getIsDisabledDay),
        newDateString,
      };
      appOnMonthChange(newMonth, e, finalMetaInfo);
      handleChangeComposedDateString(newDateString, finalMetaInfo);
    },
    [day, year, getIsOutOfRangeDay, getIsDisabledDay, appOnMonthChange, handleChangeComposedDateString],
  );
  const handleChangeDay: DSControlledDateTimePickerT.OnInternalInputsChange = useCallback(
    (newDay, e, metaInfo = defaultMetaInfo) => {
      const newDateString = getFormattedDateString({
        month,
        day: newDay,
        year,
      });
      const finalMetaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo = {
        ...metaInfo,
        ...getValidationDateStringMetaInfo(newDateString, getIsOutOfRangeDay, getIsDisabledDay),
        newDateString,
      };
      appOnDayChange(newDay, e, finalMetaInfo);
      handleChangeComposedDateString(newDateString, finalMetaInfo);
    },
    [month, year, getIsOutOfRangeDay, getIsDisabledDay, appOnDayChange, handleChangeComposedDateString],
  );
  const handleChangeYear: DSControlledDateTimePickerT.OnInternalInputsChange = useCallback(
    (newYear, e, metaInfo = defaultMetaInfo) => {
      const newDateString = getFormattedDateString({
        month,
        day,
        year: newYear,
      });
      const finalMetaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo = {
        ...metaInfo,
        ...getValidationDateStringMetaInfo(newDateString, getIsOutOfRangeDay, getIsDisabledDay),
        newDateString,
      };
      appOnYearChange(newYear, e, finalMetaInfo);
      handleChangeComposedDateString(newDateString, finalMetaInfo);
    },
    [month, day, getIsOutOfRangeDay, getIsDisabledDay, appOnYearChange, handleChangeComposedDateString],
  );

  const handleChangeHours: DSControlledDateTimePickerT.OnInternalInputsChange = useCallback(
    (newHours, e, metaInfo = defaultMetaInfo) => {
      const newTimeString = getFormattedTimeString({
        hours: newHours,
        minutes,
        // seconds,
        meridiem,
      });
      const finalMetaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo = {
        ...metaInfo,
        ...getValidationTimeStringMetaInfo(newTimeString, getIsDisabledTime),
        newTimeString,
      };
      appOnHourChange(newHours, e, finalMetaInfo);
      handleChangeComposedTimeString(newTimeString, finalMetaInfo);
    },
    [minutes, meridiem, getIsDisabledTime, appOnHourChange, handleChangeComposedTimeString],
  );
  const handleChangeMinutes: DSControlledDateTimePickerT.OnInternalInputsChange = useCallback(
    (newMinutes, e, metaInfo = defaultMetaInfo) => {
      const newTimeString = getFormattedTimeString({
        hours,
        minutes: newMinutes,
        meridiem,
      });
      const finalMetaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo = {
        ...metaInfo,
        ...getValidationTimeStringMetaInfo(newTimeString, getIsDisabledTime),
        newTimeString,
      };
      appOnMinuteChange(newMinutes, e, finalMetaInfo);
      handleChangeComposedTimeString(newTimeString, finalMetaInfo);
    },
    [hours, meridiem, getIsDisabledTime, appOnMinuteChange, handleChangeComposedTimeString],
  );
  const handleChangeMeridiem: DSControlledDateTimePickerT.OnInternalInputsChange = useCallback(
    (newMeridiem, e, metaInfo = defaultMetaInfo) => {
      const newTimeString = getFormattedTimeString({
        hours,
        minutes,
        meridiem: newMeridiem,
      });
      const finalMetaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo = {
        ...metaInfo,
        ...getValidationTimeStringMetaInfo(newTimeString, getIsDisabledTime),
        newTimeString,
      };
      appOnMeridiemChange(newMeridiem, e, finalMetaInfo);
      handleChangeComposedTimeString(newTimeString, finalMetaInfo);
    },
    [hours, minutes, getIsDisabledTime, appOnMeridiemChange, handleChangeComposedTimeString],
  );

  const handleClearAll: DSControlledDateTimePickerInternalsT.OnDSBtnClick = useCallback(
    (e) => {
      const metaInfo = {
        isAutomaticFillTrigger: false,
        isClearButtonAction: true,
        newDateString: '',
        newTimeString: '',
        newDateTimeString: '',
      };
      appOnMonthChange('', e, metaInfo);
      appOnDayChange('', e, metaInfo);
      appOnYearChange('', e, metaInfo);
      appOnHourChange('', e, metaInfo);
      appOnMinuteChange('', e, metaInfo);
      appOnMeridiemChange('', e, metaInfo);
      if (isDateTime) handleChangeComposedDateTimeString('__/__/____', '__:__ __', metaInfo);
      if (isDate) handleChangeComposedDateString('__/__/____', metaInfo);
      if (isTime) handleChangeComposedTimeString('__:__ __', metaInfo);
      trackFocusFirstSegment();
    },
    [
      appOnDayChange,
      appOnHourChange,
      appOnMeridiemChange,
      appOnMinuteChange,
      appOnMonthChange,
      appOnYearChange,
      handleChangeComposedDateString,
      handleChangeComposedDateTimeString,
      handleChangeComposedTimeString,
      isDate,
      isDateTime,
      isTime,
      trackFocusFirstSegment,
    ],
  );

  return useMemo(
    () => ({
      handleChangeComposedDateTimeString,
      handleChangeComposedDateString,
      handleChangeComposedTimeString,
      handleChangeMonth,
      handleChangeDay,
      handleChangeYear,
      handleChangeHours,
      handleChangeMinutes,
      handleChangeMeridiem,
      handleClearAll,
    }),
    [
      handleChangeComposedDateString,
      handleChangeComposedDateTimeString,
      handleChangeComposedTimeString,
      handleChangeDay,
      handleChangeHours,
      handleChangeMeridiem,
      handleChangeMinutes,
      handleChangeMonth,
      handleChangeYear,
      handleClearAll,
    ],
  );
};
